import i18next from "i18next";
import React, { useEffect, useState } from "react";
import { FIELD_REQUIRED, LOADING } from "../../../../i18n/keysTranslations";
import { Controller, useFormContext } from "react-hook-form";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { useTranslationApp } from "../../../../lib/i18next";
import { getReactHookFormNestedError } from "../../../../utils/errors";
import { useDispatchApp } from "../../../../lib/redux";
import { startGetAdvertisableAppsAdsCampaigns } from "../../../../actions/adsCampaigns";
import {
  ADS_CAMPAIGN_APP_PROMOTION_TYPE_RETARGETING_OPTION,
  TIKTOK,
} from "../../../../utils/constants";
import { INPUT_COLOR_BORDER } from "../../../../utils/colors";

const SelectAdvertisableAppsTikTok = ({
  control,
  errors,
  rules = {
    required: {
      value: true,
      message: i18next.t(FIELD_REQUIRED),
    },
  },
  name,
  label,
  disabled,
  integrationID,
  adAccountID,
  appID,
  fullWidth,
  variant = "filled",
  placeholder,
  helperText,
  onChangeAction = () => {},
  isIOS14Campaign = false,
  campaignType = "",
  sxTextField = {},
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const error = getReactHookFormNestedError(errors, name);
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { watch } = useFormContext();
  const selectedAppID = watch(name);

  const onGetAdvertisableApps = async () => {
    setLoading(true);
    const response = await dispatch(
      startGetAdvertisableAppsAdsCampaigns({
        integrationID,
        adAccountID,
        appID,
        platform: TIKTOK,
      })
    );
    if (response.ok) {
      const data = response.data;
      if (data?.length > 0) {
        setOptions(data);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    onGetAdvertisableApps();
    // eslint-disable-next-line
  }, [integrationID, adAccountID, appID]);

  const getFilteredOptions = () => {
    let initialOptions = options;
    if (
      isIOS14Campaign &&
      campaignType !== ADS_CAMPAIGN_APP_PROMOTION_TYPE_RETARGETING_OPTION
    ) {
      initialOptions = options.filter((option) => option.allowIOS14);
    }
    if (campaignType === ADS_CAMPAIGN_APP_PROMOTION_TYPE_RETARGETING_OPTION) {
      initialOptions = options.filter((option) => option.allowRetargeting);
    }

    return initialOptions;
  };

  const filteredOptions = getFilteredOptions();

  useEffect(() => {
    if (!selectedAppID || filteredOptions.length === 0) return;

    const selectedOption = filteredOptions.find(
      (option) => option?.id === selectedAppID
    );

    if (selectedOption) {
      onChangeAction(selectedOption, { syncOnly: true });
    }
  }, [selectedAppID, filteredOptions, onChangeAction]);

  return (
    <Controller
      defaultValue={""}
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => (
        <>
          <Autocomplete
            {...field}
            id={name}
            hasLabel={Boolean(label)}
            fullWidth={fullWidth}
            disabled={disabled}
            loadingText={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <CircularProgress color="inherit" size={20} />
                {t(LOADING)}
              </Box>
            }
            loading={loading}
            open={open}
            onOpen={() => {
              setOpen(true);
            }}
            onClose={() => {
              setOpen(false);
            }}
            onChange={(_, value) => {
              if (value?.id) {
                field.onChange(value.id);
                onChangeAction(value);
              } else {
                field.onChange(null);
                onChangeAction(null);
              }
            }}
            isOptionEqualToValue={(option, value) => option?.id === value}
            getOptionLabel={(option) => {
              const optionValue = filteredOptions.find(
                (opt) => opt.id === option
              );
              if (optionValue) {
                return `${optionValue?.name} - ${optionValue?.id}`;
              }
              if (typeof option === "string") {
                return option;
              }
              return `${option?.name} - ${option?.id}`;
            }}
            options={filteredOptions}
            renderOption={(props, option) => {
              return (
                <Box
                  {...props}
                  component="li"
                  key={option.value}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    cursor: "pointer",
                  }}
                >
                  <Typography variant="body2">
                    {option.name} - {option.id}
                  </Typography>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                variant={variant}
                placeholder={field?.value?.length > 0 ? "" : placeholder}
                error={!!error}
                hiddenLabel={!label}
                sx={{
                  backgroundColor: "fields.background",
                  border: `1px solid ${INPUT_COLOR_BORDER}`,
                  boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                  ...sxTextField,
                  ".MuiFormHelperText-root": {
                    ml: "0 !important",
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
          {error?.message && (
            <Typography
              variant="caption"
              color={"error"}
              sx={{
                display: "block",
              }}
            >
              {error?.message}
            </Typography>
          )}
          {helperText && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
              }}
              color={"fields.placeholder"}
              dangerouslySetInnerHTML={{
                __html: helperText,
              }}
            />
          )}
        </>
      )}
    />
  );
};

export default SelectAdvertisableAppsTikTok;
